"use client";

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocalizedRouter } from "@/hooks/useLocalizedRouter";
import { countryCodes } from "@/data/countryCodes";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { pathnameToCanonical } from "@/lib/i18nRouting";
import { requestGetOtp, requestVerifyOtp } from "@/lib/authClient";
import type { AuthUser } from "@/lib/authSession";
import { MobileBottomSheet } from "@/components/shared/MobileBottomSheet";
import {
  createLeadCreateEvent,
  DEFAULT_LOGIN_LEAD_CONTEXT,
  type LeadContext,
} from "@/lib/leadClient";
import {
  getStoredUtmChannelAttribution,
  lastTouchUtmParams,
} from "@/lib/utmChannelCookies";
import { resolveSelectedNavCityClient } from "@/lib/navCityCookie";
import { GTMEvents, GTMSubEvents, PushDataToGTM } from "@/lib/gtm";
import { LocaleLink } from "./LocaleLink";
import ConsentModal from "./ConsentModal";

type Step = "phone" | "otp";

const OTP_LENGTH = 4;
const emptyOtp = () => Array.from({ length: OTP_LENGTH }, () => "");

/** Focus the OTP row that is actually visible (mobile + desktop each render a copy). */
function focusFirstVisibleLoginOtpInput(): void {
  requestAnimationFrame(() => {
    const rows = document.querySelectorAll<HTMLElement>("[data-login-otp-row]");
    for (const row of rows) {
      const r = row.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        row.querySelector<HTMLInputElement>("input")?.focus();
        return;
      }
    }
  });
}

export type LoginDialogProps = {
  open: boolean;
  onClose: () => void;
  /**
   * Tailwind classes for the fullscreen dim layer (desktop + mobile sheet backdrop).
   * Strong blur helps keep underlying page visible but unreadable (e.g. offer-detail gate).
   */
  backdropClassName?: string;
  /**
   * Where to navigate after a successful login.
   * Defaults to `/`.
   */
  returnUrl?: string;
  /**
   * Optional hook after a successful OTP verify (after `login(user)`).
   * Receives the same `AuthUser` written to the session.
   */
  onLoggedIn?: (user: AuthUser) => void;
  /**
   * When true (default), calls `onClose()` after successful OTP so parents using
   * `open` state (e.g. header) hide the dialog. Set false when the parent unmounts
   * on login (offer-detail gate) or when `onClose` must not run on success.
   */
  closeOnSuccess?: boolean;
  /**
   * Optional lead capture context (page-specific).
   * When omitted, {@link DEFAULT_LOGIN_LEAD_CONTEXT} is used so lead/create still runs (e.g. header login).
   */
  leadContext?: LeadContext;
  pageName: string;
  pageCategory: string;
  eventCategory: string;
  customTitle?: string;
};

function DesktopDialog({
  open,
  title,
  closeLabel,
  onClose,
  children,
  lockBodyScroll = true,
  backdropClassName,
}: {
  open: boolean;
  title: string;
  closeLabel: string;
  onClose: () => void;
  children: React.ReactNode;
  /** @see MobileBottomSheet lockBodyScroll */
  lockBodyScroll?: boolean;
  backdropClassName?: string;
}) {
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const prevOverflow = lockBodyScroll ? document.body.style.overflow : "";
    if (lockBodyScroll) {
      document.body.style.overflow = "hidden";
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      if (lockBodyScroll) {
        document.body.style.overflow = prevOverflow;
      }
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, lockBodyScroll]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[120] hidden items-center justify-center p-4 md:flex ${
        backdropClassName ?? "bg-black/45 backdrop-blur-[1px]"
      }`}
    >
      <button
        type="button"
        className="absolute inset-0"
        aria-label={closeLabel}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-[28rem] overflow-hidden rounded-2xl border border-white/10 bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between gap-3 border-b border-[#E5E5E5] px-5 py-4">
          <h2 id={titleId} className="md:text-[24px] md:leading-[32px] text-[20px] leading-[28px] font-semibold text-[#231F20]">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F4F4F4]"
            aria-label={closeLabel}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>,
    document.body
  );
}

export function LoginDialog({
  open,
  onClose,
  backdropClassName,
  returnUrl = "/",
  onLoggedIn,
  closeOnSuccess = true,
  leadContext,
  pageName,
  pageCategory,
  eventCategory,
  customTitle,
}: LoginDialogProps) {
  const { push, router } = useLocalizedRouter();
  const { login } = useAuth();
  const { locale, t } = useLanguage();
  const languageCode: "en" | "ar" = locale === "ar" ? "ar" : "en";
  const [step, setStep] = useState<Step>("phone");
  const [countryCode, setCountryCode] = useState<(typeof countryCodes)[number]>(
    countryCodes[0]
  );
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState<string[]>(() => emptyOtp());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const savedCity = resolveSelectedNavCityClient();
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const [openConsentModal, setOpenConsentModal] = useState(false);
  const fullPhone = useMemo(
    () => `${countryCode.code}${phone.replace(/\D/g, "")}`,
    [countryCode.code, phone]
  );

  useEffect(() => {
    if (!open) return;
    setError("");
    setIsLoading(false);
    setResendCooldown(0);
    setStep("phone");
    setOtp(emptyOtp());
    const id = requestAnimationFrame(() => {
      phoneInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  /** Single body scroll lock: mobile sheet + desktop dialog both mount when open — double lock breaks cleanup. */
  useEffect(() => {
    if (!open || openConsentModal) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, openConsentModal]);

  const startResendCooldown = useCallback(() => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown((c) => {
        if (c <= 1) {
          clearInterval(interval);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  }, []);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 8) {
      setError(t("login.errorPhone"));
      return;
    }
    if (digits.length > 10) {
      setError(t("login.errorPhone"));
      return;
    }
    setIsLoading(true);
    try {
      const { ok, message } = await requestGetOtp(fullPhone, languageCode);
      if (!ok) {
        setError(message);
        return;
      }
      PushDataToGTM(GTMEvents.DataLayerUpdate, {
        pageName: pageName,
        pageCategory: pageCategory,
        eventCategory: eventCategory ,
        eventLabel: pageName + " > PHONE INPUT",
        stage: 3,
      });
        PushDataToGTM(GTMEvents.DataLayerUpdate, {
          pageName: pageName,
          pageCategory: pageCategory,
          eventCategory: eventCategory ?? GTMSubEvents.Click,
          eventLabel: pageName + " > OTP SENT",
          stage: 4,
        });
      setStep("otp");
      setOtp(emptyOtp());
      startResendCooldown();
      focusFirstVisibleLoginOtpInput();
    } catch {
      setError(t("login.errorSendOtp"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (
    index: number,
    value: string,
    inputEl: HTMLInputElement
  ) => {
    const row = inputEl.closest("[data-login-otp-row]");
    const inputs = row?.querySelectorAll<HTMLInputElement>("input");

    if (value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, OTP_LENGTH).split("");
      const newOtp = [...otp];
      digits.forEach((d, i) => {
        if (index + i < OTP_LENGTH) newOtp[index + i] = d;
      });
      setOtp(newOtp);
      const focusIdx = Math.min(index + digits.length, OTP_LENGTH - 1);
      requestAnimationFrame(() => {
        inputs?.[focusIdx]?.focus();
      });
      return;
    }
    const digit = value.replace(/\D/g, "").slice(-1);
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    if (digit && index < OTP_LENGTH - 1) {
      requestAnimationFrame(() => {
        inputs?.[index + 1]?.focus();
      });
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const row = e.currentTarget.closest("[data-login-otp-row]");
      const inputs = row?.querySelectorAll<HTMLInputElement>("input");
      const newOtp = [...otp];
      newOtp[index - 1] = "";
      setOtp(newOtp);
      requestAnimationFrame(() => {
        inputs?.[index - 1]?.focus();
      });
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const code = otp.join("");
    if (code.length !== OTP_LENGTH) {
      setError(t("login.errorOtp"));
      return;
    }
    setIsLoading(true);
    try {
      const result = await requestVerifyOtp(fullPhone, code);
      console.log("result in handleVerifyOtp", result);
      if (!result.ok) {
        setError(result.message);
        PushDataToGTM(GTMEvents.DataLayerUpdate, {
          pageName: pageName,
          pageCategory: pageCategory,
          eventCategory: eventCategory ?? GTMSubEvents.Click,
          eventLabel: pageName + " > INCORRECT OTP",
          stage: 5,
        });
        return;
      }
      const digits = phone.replace(/\D/g, "");
      {
        const leadCtx = leadContext ?? DEFAULT_LOGIN_LEAD_CONTEXT;
        let utmParams: Record<string, string> | undefined;
        try {
          const { last } = getStoredUtmChannelAttribution();
          const { utm_id, ...rest } = lastTouchUtmParams(last);
          void utm_id;
          utmParams = rest;
        } catch {
          utmParams = undefined;
        }
        // Await before navigation so this request isn't aborted by route change.
        await createLeadCreateEvent({
          context: leadCtx,
          verified: 1,
          cityId: savedCity?.id?.toString() ?? "",
          city: savedCity?.nameEn ?? "",
          user: result.user,
          customerId: result.user.customerId ?? "NA",
          email: result.user.email ?? "NA",
          customerName: result.user.name ?? "NA",
          phoneCode: countryCode.code,
          phoneNumber: digits,
          utmParams,
          consent: true,
          accept: true,
        });
      }
      PushDataToGTM(GTMEvents.DataLayerUpdate, {
        pageName: pageName,
        pageCategory: pageCategory,
        eventCategory: eventCategory,
        eventLabel: pageName + " > OTP VERIFIED",
        stage: 7,
      });
      login(result.user);
      onLoggedIn?.(result.user);
      if (closeOnSuccess) {
        onClose();
      }
      if (returnUrl.startsWith("/") && !returnUrl.startsWith("//")) {
        push(pathnameToCanonical(returnUrl));
      } else {
        router.push(returnUrl);
      }
      router.refresh();
    } catch {
      setError(t("login.errorInvalidOtp"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setError("");
    setIsLoading(true);
    PushDataToGTM(GTMEvents.DataLayerUpdate, {
      pageName: pageName,
      pageCategory: pageCategory,
      eventCategory: eventCategory,
      eventLabel: pageName + " > RESEND OTP",
      stage: 6,
    });
    try {
      const { ok, message } = await requestGetOtp(fullPhone, languageCode);
      if (!ok) {
        setError(message);
        return;
      }
      startResendCooldown();
    } catch {
      setError(t("login.errorResend"));
    } finally {
      setIsLoading(false);
    }
  };

  const title =
    step === "phone" ? customTitle === "offer" ? t("login.offerLoginTitle") : t("login.signIn") : customTitle === "offer" ? t("login.offerLoginSubtitle") : t("login.verifyTitle");
  const closeLabel = t("login.closeDialogAria");

  const content = (
    <div>
      <p className="mb-6 text-sm text-[#6B7280]">
        {step === "phone"
          ? t("login.phoneSubtext")
          : `${t("login.otpSubtext")} ${fullPhone}`}
      </p>

      {error ? (
        <div className="mb-6 rounded-lg bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      ) : null}

      {step === "phone" ? (
        <form onSubmit={handleSendOtp} className="space-y-6">
          <div>
            <label className="mb-2 block text-sm font-medium text-[#130F0E]">
              {t("login.phoneLabel")}
            </label>
            <div className="flex gap-2">
              <select
                value={countryCode.code}
                onChange={(e) => {
                  const found = countryCodes.find(
                    (c) => c.code === e.target.value
                  );
                  if (found) setCountryCode(found);
                }}
                className="w-32 rounded-lg border border-[#E5E5E5] bg-white px-3 py-3.5 text-sm text-[#130F0E] focus:border-[#DD0428] focus:outline-none focus:ring-2 focus:ring-[#DD0428]/20"
              >
                {countryCodes.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.code}
                  </option>
                ))}
              </select>
              <input
                ref={phoneInputRef}
                value={phone}
                onChange={(e) =>
                  setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))
                }
                onPaste={(e) => {
                  e.preventDefault();
                  const text = e.clipboardData.getData("text");
                  setPhone(text.replace(/\D/g, "").slice(0, 10));
                }}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={10}
                autoComplete="tel"
                placeholder={t("login.phonePlaceholder")}
                className="flex-1 rounded-lg border border-[#E5E5E5] bg-white px-3 py-3.5 text-sm text-[#130F0E] focus:border-[#DD0428] focus:outline-none focus:ring-2 focus:ring-[#DD0428]/20"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-lg bg-[#BD2226] py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#C00322] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? t("login.sending") : t("login.sendOtp")}
          </button>
        </form>
      ) : (
        <form onSubmit={handleVerifyOtp} className="space-y-6">
          <div>
            <label className="mb-2 block text-sm font-medium text-[#130F0E]">
              {t("login.otpLabel")}
            </label>
            <div className="flex gap-2" data-login-otp-row>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  type="text"
                  value={digit}
                  maxLength={i === 0 ? OTP_LENGTH : 1}
                  onChange={(e) =>
                    handleOtpChange(i, e.target.value, e.target)
                  }
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  inputMode="numeric"
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  aria-label={`OTP digit ${i + 1} of ${OTP_LENGTH}`}
                  className="h-12 w-12 rounded-lg border border-[#E5E5E5] bg-white text-center text-lg font-semibold text-[#130F0E] focus:border-[#DD0428] focus:outline-none focus:ring-2 focus:ring-[#DD0428]/20"
                />
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-lg bg-[#BD2226] py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#C00322] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? t("login.verifying") : t("login.verifySignIn")}
          </button>

          <div className="flex items-center justify-between gap-3 text-sm">
            <button
              type="button"
              onClick={() => {
                setStep("phone");
                setError("");
                setOtp(emptyOtp());
                requestAnimationFrame(() => phoneInputRef.current?.focus());
              }}
              className="font-semibold text-[#130F0E] hover:underline"
            >
              {t("login.changeNumber")}
            </button>
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={isLoading || resendCooldown > 0}
              className="font-semibold text-[#BD2226] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {resendCooldown > 0
                ? `${t("login.resendIn")} ${resendCooldown}${t("login.secondsAbbrev")}`
                : t("login.resendOtp")}
            </button>
          </div>
        </form>
      )}
        <div className="text-[13px] leading-[20px] text-black-54 text-center md:px-[20px] mt-[50px]">
              {!fullPhone && (
                <div className="mb-1">
                  {t("contact.byContinuingYouAgreeTo")}
                </div>
              )}

              <span>
                {fullPhone && (
                  <>{t("contact.byContinuingYouAgreeTo")} </>
                )}

                {[
                  <LocaleLink
                    key="terms"
                    href="/terms-and-conditions"
                    className="underline font-semibold"
                    onClick={onClose}
                  >
                    {t("contact.legalTermsLink")?.toLowerCase()}
                  </LocaleLink>,

                  <LocaleLink
                    key="privacy"
                    href="/privacy-policy"
                    className="underline font-semibold"
                    onClick={onClose}
                  >
                    {t("contact.legalPrivacyLink")?.toLowerCase()}
                  </LocaleLink>,

                  phone && phone.length > 5 && (
                    <span
                      key="marketing"
                      onClick={()=>{
                        //onClose();
                        setOpenConsentModal(true)
                      }}
                      className="underline font-semibold cursor-pointer"
                    >
                      {t("contact.marketingCommunications")?.toLowerCase()}
                    </span>
                  ),
                ]
                  .filter(Boolean)
                  .map((item, index, array) => (
                    <span key={index}>
                      {item}
                      {index < array.length - 2 && t("contact.comma")}
                      {index === array.length - 2 && ` ${t("contact.and")} `}
                    </span>
                  ))}
              </span>
            </div>
    </div>
  );

  return (
    <>
      {openConsentModal ? 
      <ConsentModal 
      mobile={fullPhone} 
      locale={locale} 
      onModelClose={() => setOpenConsentModal(false)} />
      :
      <>
      <MobileBottomSheet
        open={open}
        title={title}
        closeLabel={closeLabel}
        onClose={onClose}
        lockBodyScroll={false}
        backdropClassName={
          backdropClassName ?? "bg-black/45 backdrop-blur-[1px]"
        }
      >
        {content}
      </MobileBottomSheet>
      <DesktopDialog
        open={open}
        title={title}
        closeLabel={closeLabel}
        onClose={onClose}
        lockBodyScroll={false}
        backdropClassName={backdropClassName}
      >
        {content}
      </DesktopDialog>
      </>
      }
    </>
  );
}

